import type { ComponentProps } from 'react';
import * as SwipeActionsPrimitive from '@ncdai/react-swipe-actions';

import { cn } from '../../lib/utils';

/**
 * The @ncdai/swipe-actions registry item (`shadcn add @ncdai/swipe-actions`),
 * rewritten for this project the way wheel-picker.tsx was: the registry's
 * classes are Tailwind v4 (bare `data-armed:` variants, `size-*` on svg), and
 * we are on v3, so the same look is spelled with arbitrary variants.
 *
 * A row is `SwipeItem` around one `SwipeActions` per side and a `SwipeContent`;
 * `SwipeRoot` around a list makes its rows close one another. Paint goes on
 * `SwipeAction` (it floods the row on a full swipe), layout on its content —
 * which needs a width, since it is what the row measures to know how far to
 * open. See the primitive's README for the rest.
 */
type SwipeSide = SwipeActionsPrimitive.SwipeSide;
type SwipeState = SwipeActionsPrimitive.SwipeState;

const SwipeRoot = SwipeActionsPrimitive.SwipeRoot;

const SwipeItem = SwipeActionsPrimitive.SwipeItem;

const SwipeActions = SwipeActionsPrimitive.SwipeActions;

function SwipeAction({
  className,
  contentClassName,
  children,
  ...props
}: ComponentProps<typeof SwipeActionsPrimitive.SwipeAction> & {
  /**
   * Sizes the visible slice of the action — its width is how much the row
   * opens for it — where the default 80px is too wide for the strip's count.
   */
  contentClassName?: string;
}) {
  return (
    <SwipeActionsPrimitive.SwipeAction
      className={cn(
        'select-none whitespace-nowrap bg-secondary text-xs font-medium text-secondary-foreground',
        'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-foreground',
        'data-[armed]:brightness-90',
        '[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*="size-"])]:size-4',
        className
      )}
      {...props}
    >
      <SwipeActionsPrimitive.SwipeActionContent
        className={cn('min-w-20 flex-col items-center justify-center gap-1.5 px-4', contentClassName)}
      >
        {children}
      </SwipeActionsPrimitive.SwipeActionContent>
    </SwipeActionsPrimitive.SwipeAction>
  );
}

/**
 * Unlike the registry's, this paints no background of its own: the strips are
 * positioned beside the content and clipped by the item, never under it, and
 * an opaque content would hide whatever the list paints behind a row (the
 * highlight on the one just navigated back to). Callers that overlap content
 * and actions must add their own.
 */
function SwipeContent({
  className,
  ...props
}: ComponentProps<typeof SwipeActionsPrimitive.SwipeContent>) {
  return (
    <SwipeActionsPrimitive.SwipeContent
      className={cn('data-[dragging]:cursor-grabbing', className)}
      {...props}
    />
  );
}

export { SwipeAction, SwipeActions, SwipeContent, SwipeItem, SwipeRoot };
export type { SwipeSide, SwipeState };
